<template>
  <v-card
    v-if="opportunity && opportunity.id"
    class="candidate applications application-form"
    :height="height"
    :maxHeight="height"
    :width="width"
    :maxWidth="width"
  >
    <v-card-title
      class="application-form__title pa-0"
      :class="{ 'pt-5': $vuetify.breakpoint.xsOnly }"
      style="flex: 0 0 auto;"
    >
      <v-btn
        @click="$emit('close')"
        :dark="$vuetify.breakpoint.smAndUp"
        class="mt-4"
        absolute
        right
        flat
        top
        fab
      >
        <v-icon dark>mdi-close</v-icon>
      </v-btn>
      <OpportunityCoverImage
        v-if="$vuetify.breakpoint.smAndUp"
        :opportunity="opportunity"
        :height="coverImageHeight"
      />
      <v-layout column>
        <v-flex d-flex class="text-xs-center step-avatar--overlap">
          <div v-if="opportunity.organization">
            <v-avatar
              :size="80"
              :color="!application && incompleteRequirements.length ? 'success' : markerColor"
            >
              <div
                class="display-3 pa-5 font-weight-bold white--text"
                v-if="!application && incompleteRequirements.length"
              >
                {{ incompleteRequirements.length }}
              </div>
              <v-icon
                v-else-if="!!$vuetify.icons[markerIcon]"
                v-text="`$vuetify.icons.${markerIcon}`"
                size="50"
                dark
              />
              <v-icon v-else dark size="60">{{ markerIcon }}</v-icon>
            </v-avatar>
          </div>
        </v-flex>

        <v-flex class="text-xs-center pb-2 px-2">
          <div class="title">{{ opportunity.name }}</div>
          <div
            v-if="opportunity.organization"
            :class="themes.candidate.light.mutedText"
            class="subheading"
          >
            {{ opportunity.organization.name }}
          </div>
        </v-flex>
      </v-layout>
    </v-card-title>

    <v-divider />

    <v-alert
      :color="!application && incompleteRequirements.length ? 'success' : markerColor"
      class="ma-0 py-1 text-xs-center"
      :value="!loading"
    >
      <span>
        {{ !application && incompleteRequirements.length ? 'Steps remaining' : markerText }}
      </span>
    </v-alert>

    <transition
      enter-active-class="animated fadeIn"
      leave-active-class="animated fadeOut"
      mode="out-in"
    >
      <v-card-text
        v-if="!!application"
        key="existing-application-notification"
        class="text-xs-center pt-4 px-4"
        style="height: 100%;"
      >
        <div class="text-xs-right">
          <v-btn color="success" block flat :to="`/candidate/applications/${application.id}`">
            Track Application
          </v-btn>
        </div>
      </v-card-text>

      <v-card-text
        class="pa-0"
        v-else-if="!loading && checklist"
        key="application-stepper"
        id="scroll-target"
        style="height: 100%;"
        ref="steps"
      >
        <v-stepper v-model="stepper" class="elevation-0" vertical v-scroll:#scroll-target="scroll">
          <template v-for="(requirement, i) in requirementsChecklist">
            <v-stepper-step
              :key="`requirement-stepper-step-${i}`"
              :complete="isRequirementSatisfied(requirement) || isRequirementModified(requirement)"
              :step="i + 1"
              :color="requirementColor(requirement)"
              edit-icon="mdi-check"
              :editable="
                isRequirementSatisfied(requirement) ||
                  isRequirementModified(requirement) ||
                  i === nextIncompleteRequirement
              "
            >
              {{ labelFor(requirement.key) }}
              <small
                v-if="isRequirementSatisfied(requirement) || isRequirementModified(requirement)"
              >
                <CompletedStepSummary :step="requirement.key" :profile="profile" />
              </small>
              <small v-else-if="requirement.status === 'possible'">Review information</small>
              <small v-else>More information required</small>
            </v-stepper-step>
            <v-stepper-content :key="`requirement-stepper-content-${i}`" :step="i + 1">
              <v-card flat>
                <div
                  v-if="profile && stepComponentLookup[requirement.key]"
                  :is="stepComponentLookup[requirement.key]"
                  :profile="profile"
                  @input="finalizeStep(requirement)"
                  :active="stepper === i + 1"
                />
              </v-card>
            </v-stepper-content>
          </template>

          <!-- <v-stepper-step -->
          <!--   :step="requirementsChecklist.length + 1" -->
          <!--   :color="isEligible ? 'success' : 'warning'" -->
          <!--   :editable="nextIncompleteRequirement < 0" -->
          <!-- > -->
          <!--   Apply -->
          <!-- </v-stepper-step> -->

          <!-- <v-stepper-content -->
          <!--   key="requirement-stepper-content-apply" -->
          <!--   :step="requirementsChecklist.length + 1" -->
          <!-- > -->
          <!--   <v-card flat> -->
          <!--     <ApplyStep -->
          <!--       v-if="stepper === requirementsChecklist.length + 1" -->
          <!--       :opportunity="opportunity" -->
          <!--     /> -->
          <!--   </v-card> -->
          <!-- </v-stepper-content> -->
        </v-stepper>
      </v-card-text>
    </transition>

    <v-progress-linear
      :color="checklistProgress >= 100 ? markerColor : 'success'"
      :value="checklistProgress"
      class="ma-0"
      :height="5"
    />

    <v-card-actions
      v-if="!loading"
      :style="`max-height: ${actionsHeight}; height: ${actionsHeight};`"
      :class="{ 'application-form__footer--expand': expandActions }"
      class="px-4 application-form__footer"
    >
      <v-btn
        :color="checklistProgress >= 100 ? markerColor : 'success'"
        absolute
        small
        fab
        @click="toggleExpandedActions"
        class="application-form__footer-toggle"
      >
        <v-icon>mdi-{{ expandActions ? 'chevron-down' : 'chevron-up' }}</v-icon>
      </v-btn>
      <ApplicationFormFooter
        :color="checklistProgress >= 100 ? markerColor : 'success'"
        :opportunity="opportunity"
        :progress="checklistProgress"
        @click="toggleExpandedActions"
      />
    </v-card-actions>
  </v-card>
</template>

<script>
// Components
import AgeStep from '@/components/candidate/applications/application_form_steps/AgeStep'
import ApplicationFormFooter from '@/components/candidate/applications/ApplicationFormFooter'
import ApplyButton from '@/components/candidate/applications/ApplyButton'
import ApplyStep from '@/components/candidate/applications/application_form_steps/ApplyStep'
import CompletedStepSummary from '@/components/candidate/applications/application_form_steps/CompletedStepSummary'
import ExperienceStep from '@/components/candidate/applications/application_form_steps/ExperienceStep'
import GenderStep from '@/components/candidate/applications/application_form_steps/GenderStep'
import LocationStep from '@/components/candidate/applications/application_form_steps/LocationStep'
import NationalityStep from '@/components/candidate/applications/application_form_steps/NationalityStep'
import OpportunityCoverImage from '@/components/guest/opportunities/OpportunityCoverImage'
import OpportunityRequirementsGuide from '@/components/guest/opportunities/OpportunityRequirementsGuide'
import PhaseStepper from '@/components/candidate/applications/PhaseStepper'
import QualificationsStep from '@/components/candidate/applications/application_form_steps/QualificationsStep'
import StudyRightsStep from '@/components/candidate/applications/application_form_steps/StudyRightsStep'
import WorkRightsStep from '@/components/candidate/applications/application_form_steps/WorkRightsStep'
import ProfessionalQualificationsStep from '@/components/candidate/applications/application_form_steps/ProfessionalQualificationsStep'

// Helpers
import _ from 'lodash'
import { mapGetters } from 'vuex'

// Mixins
import { EligibilityChecklist } from '@/mixins/EligibilityChecklist'
import { OpportunityMapMarkerImage } from '@/mixins/OpportunityMapMarkerImage.js'

// Data
const stepComponentLookup = {
  age: AgeStep,
  experience: ExperienceStep,
  gender: GenderStep,
  location: LocationStep,
  nationality: NationalityStep,
  professional_qualifications: ProfessionalQualificationsStep,
  qualifications: QualificationsStep,
  study_right: StudyRightsStep,
  work_right: WorkRightsStep,
}

export default {
  mixins: [EligibilityChecklist, OpportunityMapMarkerImage],

  components: {
    AgeStep,
    ApplicationFormFooter,
    ApplyButton,
    ApplyStep,
    CompletedStepSummary,
    ExperienceStep,
    GenderStep,
    LocationStep,
    NationalityStep,
    OpportunityCoverImage,
    OpportunityRequirementsGuide,
    PhaseStepper,
    QualificationsStep,
    StudyRightsStep,
    WorkRightsStep,
  },

  created() {
    let promises = [
      this.$store.dispatch('candidate/eligibilities/checkEligibility', this.opportunity),
    ]
    if (!_.get(this, 'profile.id')) {
      promises.push(this.$store.dispatch('candidate/profile/fetch'))
    }
    if (!_.get(this, 'application.id')) {
      promises.push(this.$store.dispatch('candidate/applications/fetchAll'))
    }

    Promise.all(promises).finally(() => {
      this.loading = false
      this.$nextTick(this.moveStepper)
    })
  },

  props: {
    opportunity: { type: Object, required: true },
    width: { type: String },
    height: { type: String },
    closable: { type: Boolean, default: false },
  },

  data() {
    return {
      expandActions: false,
      actionsHeight: '68px',
      loading: true,
      offsetTop: 0,
      stepComponentLookup,
      stepper: 1,
    }
  },

  computed: {
    ...mapGetters({ profile: 'candidate/profile/model' }),

    application() {
      return this.$store.getters['candidate/applications/forOpportunity'](this.opportunity.id)
    },

    isEligible() {
      return this.eligibility && this.eligibility.eligible
    },

    coverImageHeight() {
      return `${Math.max(50, 200 - this.offsetTop)}px`
    },
  },

  methods: {
    labelFor(key) {
      if (typeof key !== 'string') return key
      if (key === 'study_right') return 'Study rights'
      if (key === 'work_right') return 'Work rights'
      if (key === 'qualifications') return 'Qualifications or study programmes'
      return key.capitalize().replace('_', ' ')
    },

    finalizeStep(requirement) {
      this.$store.dispatch('candidate/eligibilities/checkEligibility', this.opportunity)
      this.pushModifiedRequirement(requirement)
      this.moveStepper()
    },

    moveStepper() {
      if (this.hasIncompleteRequirements) {
        this.stepper = this.nextIncompleteRequirement + 1
        return
      }

      if (this.requirementsChecklist.length) {
        this.stepper = this.requirementsChecklist.length + 1
        return
      }
    },

    scroll(e) {
      this.offsetTop = Math.max(this.offsetTop, e.target.scrollTop)
    },

    toggleExpandedActions() {
      this.offsetTop = 150
      this.$nextTick(() => {
        this.expandActions = !this.expandActions
        if (this.expandActions) {
          const height = _.get(this, '$refs.steps.clientHeight')
          this.actionsHeight = height ? `${height + 72}px` : '50%'
        }
        if (!this.expandActions) {
          this.actionsHeight = '68px'
        }
      })
    },
  },
}
</script>

<style lang="scss">
.candidate.applications.application-form {
  .step-avatar--overlap {
    position: absolute;
    transform: translate(-50%, -40%);
    left: 50%;

    + * {
      margin-top: 4.2rem;
    }
  }

  .v-stepper--vertical .v-stepper__content {
    width: 95%;
  }

  .application-form__footer {
    position: relative;
    transition-property: height, max-height;
    transition: all 0.4s ease-out;

    &.application-form__footer--expand {
      .application-form__footer-toggle {
        top: 2rem;
      }
    }

    .application-form__footer-toggle {
      transform: translate(-50%, -50%);
      top: 0;
      left: 50%;
    }
  }
}
</style>
